/**
 * Controller State
 * 
 * Stores information about the current controller state, and its components.
 */
export interface DualShock4State {
  /** Interface used for communication (USB/Bluetooth) */
  interface : DualShock4Interface

  /** Battery capacity (0-100), or `null` when it cannot be determined. */
  batteryCapacity : number | null
  /** Current battery state. */
  batteryStatus : BatteryStatus

  /** Whether the controller reports a cable connected, independently of the HID transport. */
  cableConnected : boolean
  /** Raw controller flag indicating a device connected to the EXT accessory port. */
  externalDeviceConnected : boolean
  /** Whether the controller reports headphones connected to its 3.5 mm jack. */
  headphonesConnected : boolean
  /** Whether the controller reports a microphone connected to its 3.5 mm jack. */
  microphoneConnected : boolean

  /** Analog positions */
  axes : DualShock4AnalogState
  /** Buttons pressed */
  buttons : DualShock4ButtonState
  /** Touchpad */
  touchpad : DualShock4Touchpad

  /** Raw 16-bit sensor timestamp (0-65535), or `null` before a full input report. Wraps at 65536. */
  sensorTimestamp : number | null
  /** Raw 6-bit input report counter (0-63), or `null` before the first input report. Wraps at 64. */
  reportCounter : number | null
  /** Browser event timestamp of the last report, in milliseconds. */
  timestamp : number
}

/** Current battery state. */
export type BatteryStatus = 'discharging' | 'charging' | 'full' | 'error' | 'unknown'

/**
 * Button State
 * 
 * Stores information about the buttons that are currently being held.
 */
export interface DualShock4ButtonState {
  /** Triangle Button */
  triangle : boolean
  /** Circle Button */
  circle : boolean
  /** Cross Button */
  cross : boolean
  /** Square Button */
  square : boolean

  /** D-Pad Up */
  dPadUp : boolean
  /** D-Pad Right */
  dPadRight : boolean
  /** D-Pad Down */
  dPadDown : boolean
  /** D-Pad Left */
  dPadLeft : boolean

  /** L1 Button */
  l1 : boolean
  /** L2 Trigger (non-analog value) */
  l2 : boolean
  /** L3 Button */
  l3 : boolean

  /** R1 Button */
  r1 : boolean
  /** R2 Trigger (non-analog value) */
  r2 : boolean
  /** R3 Button */
  r3 : boolean

  /** Options Button */
  options : boolean
  /** Share Button */
  share : boolean
  /** PS Button */
  playStation : boolean

  /** Touchpad Button */
  touchPadClick : boolean
}

/**
 * Analog State
 * 
 * Stores information for analog axes.
 * 
 * - Values for thumbsticks are stored using the range **-1.0** (left, top) to **1.0** (right, bottom).
 *
 * - Values for triggers use the range **0.0** (released) to **1.0** (pressed)
 *
 * - Values for accelerometer and gyroscope use the raw input from the sensors.
 */
export interface DualShock4AnalogState {
  /** Left Stick Horizontal position. */
  leftStickX : number
  /** Left Stick Vertical position. */
  leftStickY : number

  /** Right Stick Horizontal position. */
  rightStickX : number
  /** Right Stick Vertical position. */
  rightStickY : number

  /** Left trigger analog value */
  l2 : number
  /** Right trigger analog value */
  r2 : number

  /** Accelerometer X */
  accelX : number
  /** Accelerometer Y */
  accelY : number
  /** Accelerometer Z */
  accelZ : number

  /** Angular velocity X */
  gyroX : number
  /** Angular velocity Y */
  gyroY : number
  /** Angular velocity Z */
  gyroZ : number
}

/** Touchpad State */
export interface DualShock4Touchpad {
  /** Active contacts from the last frame in the most recent full input report. */
  touches : DualShock4TouchpadTouch[]
  /** Complete frames from the most recent full report, in reported order; not accumulated history. */
  frames : DualShock4TouchpadFrame[]
  /** Last frame's raw 8-bit counter (0-255), or null when no frames are available. */
  frameCounter : number | null
}

/** One reported touchpad frame, including frames with no active contacts. */
export interface DualShock4TouchpadFrame {
  /** Raw 8-bit frame counter; wraps from 255 to 0. */
  frameCounter : number
  /** Active contacts in this frame. */
  touches : DualShock4TouchpadTouch[]
}

/**
 * Touchpad Touch Information
 * 
 * The touchpad's resolution is 1920x943.
 */
export interface DualShock4TouchpadTouch {
  /** Touch ID. Changes with every new touch. */
  touchId : number
  /** X Position. */
  x : number
  /** Y Position. */
  y : number
}

/**
 * Current Interface
 */
export enum DualShock4Interface {
  Disconnected = 'none',
  /** The controller is connected over USB */
  USB = 'usb',
  /** The controller is connected over BT */
  Bluetooth = 'bt'
}

/**
 * Default / Initial State
 * @ignore
 */
export function createDefaultState () : DualShock4State {
  return {
    interface: DualShock4Interface.Disconnected,
    batteryCapacity: null,
    batteryStatus: 'unknown',
    cableConnected: false,
    externalDeviceConnected: false,
    headphonesConnected: false,
    microphoneConnected: false,

    axes: {
      leftStickX: 0,
      leftStickY: 0,
      rightStickX: 0,
      rightStickY: 0,

      l2: 0,
      r2: 0,

      accelX: 0,
      accelY: 0,
      accelZ: 0,

      gyroX: 0,
      gyroY: 0,
      gyroZ: 0
    },

    buttons: {
      triangle: false,
      circle: false,
      cross: false,
      square: false,

      dPadUp: false,
      dPadRight: false,
      dPadDown: false,
      dPadLeft: false,

      l1: false,
      l2: false,
      l3: false,

      r1: false,
      r2: false,
      r3: false,

      options: false,
      share: false,
      playStation: false,
      touchPadClick: false
    },

    touchpad: {
      touches: [],
      frames: [],
      frameCounter: null
    },

    sensorTimestamp: null,
    reportCounter: null,
    timestamp: -1
  }
}

/** A detected transport for an open controller session. @internal */
export type ControllerTransport = DualShock4Interface.USB | DualShock4Interface.Bluetooth
